import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackingApi } from '../api/services';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Truck, Loader2, RefreshCw, Search, Package, CheckCircle2,
  MapPin, Clock, AlertTriangle, RotateCcw,
} from 'lucide-react';

const STATUS_CONFIG = {
  delivered:        { label: 'Delivered',        color: 'bg-green-100 text-green-700',  icon: CheckCircle2 },
  in_transit:       { label: 'In Transit',       color: 'bg-blue-100 text-blue-700',    icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-orange-100 text-orange-700', icon: MapPin },
  booked:           { label: 'Booked',           color: 'bg-gray-100 text-gray-700',    icon: Package },
  customs:          { label: 'Customs',          color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  returned:         { label: 'Returned',         color: 'bg-red-100 text-red-700',      icon: RotateCcw },
  undelivered:      { label: 'Undelivered',      color: 'bg-red-100 text-red-700',      icon: AlertTriangle },
  not_found:        { label: 'Not Found',        color: 'bg-gray-100 text-gray-500',    icon: Search },
  unknown:          { label: 'Unknown',          color: 'bg-gray-100 text-gray-500',    icon: Clock },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
      <cfg.icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function CarrierTracking() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState({ statusBreakdown: [], uncheckedCount: 0, shipments: [] });
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = (statusOverride, searchOverride) => {
    const s = statusOverride !== undefined ? statusOverride : filter;
    const q = searchOverride !== undefined ? searchOverride : search;
    setLoading(true);
    trackingApi.carrierDashboard({ status: s, search: q || undefined })
      .then((res) => setData(res.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const triggerPoll = async () => {
    setPolling(true);
    try {
      const res = await trackingApi.carrierPoll();
      const s = res.data.stats;
      toast.success(`Polled ${s.polled} shipments: ${s.updated} updated, ${s.delivered} delivered, ${s.errors} errors`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Poll failed');
    } finally {
      setPolling(false);
    }
  };

  const refreshOne = async (shipmentId) => {
    try {
      const { data } = await trackingApi.carrierRefresh(shipmentId);
      toast.success(data.tracking?.skipped
        ? 'Status is final (delivered/returned) — not re-checked'
        : 'Refreshed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Refresh failed');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    load(undefined, searchInput);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    load(newFilter);
  };

  const totalTracked = data.statusBreakdown.reduce((sum, r) => sum + Number(r.count), 0);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Carrier Tracking
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            India Post tracking status for all shipments — auto-polled every 4 hours
          </p>
        </div>
        {/* Poll-now action is admin-only — backend POST stays role-gated.
            Hide for everyone else so dispatch_user / ops don't get a 403. */}
        {isAdmin() && (
          <button
            onClick={triggerPoll}
            disabled={polling}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {polling ? 'Polling...' : 'Poll Now'}
          </button>
        )}
      </div>

      {/* Status summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <button
          onClick={() => handleFilterChange('all')}
          className={`bg-white rounded-xl border p-3 text-left transition hover:shadow-sm ${filter === 'all' ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200'}`}
        >
          <div className="text-xs text-gray-500 uppercase font-medium">Total Tracked</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{totalTracked}</div>
        </button>
        {data.statusBreakdown.map((row) => {
          const cfg = STATUS_CONFIG[row.carrier_status] || STATUS_CONFIG.unknown;
          return (
            <button
              key={row.carrier_status}
              onClick={() => handleFilterChange(row.carrier_status)}
              className={`bg-white rounded-xl border p-3 text-left transition hover:shadow-sm ${filter === row.carrier_status ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200'}`}
            >
              <div className="text-xs text-gray-500 uppercase font-medium flex items-center gap-1">
                <cfg.icon className="w-3 h-3" />
                {cfg.label}
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{row.count}</div>
            </button>
          );
        })}
        {data.uncheckedCount > 0 && (
          <button
            onClick={() => handleFilterChange('unchecked')}
            className={`bg-white rounded-xl border p-3 text-left transition hover:shadow-sm ${filter === 'unchecked' ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200'}`}
          >
            <div className="text-xs text-gray-500 uppercase font-medium">Unchecked</div>
            <div className="text-2xl font-bold text-amber-600 mt-1">{data.uncheckedCount}</div>
          </button>
        )}
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-4 flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by tracking number, order ID, or customer..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button type="submit" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
          Search
        </button>
        {(search || filter !== 'all') && (
          <button
            type="button"
            onClick={() => { setSearchInput(''); setSearch(''); setFilter('all'); load('all', ''); }}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        )}
      </form>

      {/* Shipments table */}
      {loading ? (
        <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading...</div>
      ) : data.shipments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          <Truck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          {filter !== 'all' || search ? 'No shipments match your filter.' : 'No shipments with tracking numbers found.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Tracking #</th>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">File name</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Carrier Status</th>
                <th className="text-left px-3 py-2">Last Event</th>
                <th className="text-left px-3 py-2">Last Checked</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.shipments.map((s) => (
                <tr key={s.shipment_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">{s.tracking_number}</td>
                  <td className="px-3 py-2">
                    <Link to={`/orders/${s.order_id}`} className="text-blue-600 hover:underline text-xs">
                      {s.order_id_external}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-600 max-w-[180px] truncate" title={s.intake_filename || ''}>
                    {s.intake_filename || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{s.customer_name}</td>
                  <td className="px-3 py-2 text-xs">{s.vendor_name || '—'}</td>
                  <td className="px-3 py-2">
                    {s.carrier_status ? (
                      <StatusBadge status={s.carrier_status} />
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not checked</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 max-w-[200px] truncate" title={s.carrier_last_event}>
                    {s.carrier_last_event || '—'}
                    {s.carrier_last_event_at && (
                      <div className="text-[10px] text-gray-400">
                        {format(new Date(s.carrier_last_event_at), 'dd MMM yyyy HH:mm')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-400">
                    {s.carrier_last_checked_at ? (
                      <span title={format(new Date(s.carrier_last_checked_at), 'dd MMM yyyy HH:mm:ss')}>
                        {formatDistanceToNow(new Date(s.carrier_last_checked_at), { addSuffix: true })}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => refreshOne(s.shipment_id)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Refresh carrier status"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.shipments.length >= 500 && (
            <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-t">
              Showing first 500 results. Use search or filters to narrow down.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
